import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { GripVertical, Save, RotateCcw, Eye, Palette, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useStore } from "@/contexts/StoreContext";

const HOMEPAGE_SECTIONS = [
  { key: "hero",         label: "Banners (Hero)" },
  { key: "categories",   label: "Categories" },
  { key: "best_sellers", label: "Best Sellers" },
  { key: "trending",     label: "Trending" },
  { key: "recommended",  label: "Featured" },
  { key: "offer_banner", label: "Offers" },
];

export default function UICustomization() {
  const { user } = useAuth();
  const { refresh } = useStore();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Sprint — Category tab (restored). Reads/writes `settings.commerce.storefront`
  // so the same values persist across UI Customization AND Store Settings →
  // Storefront tab. Loading in parallel with /ui-customization.
  const [sf, setSf] = useState(null);
  const [sfDirty, setSfDirty] = useState(false);
  const [sfCommerce, setSfCommerce] = useState({});

  useEffect(() => {
    if (!isSuperAdmin) return;
    Promise.all([
      api.get("/ui-customization"),
      api.get("/store/settings").catch(() => ({ data: {} })),
    ])
      .then(([uiRes, stRes]) => {
        const s = uiRes.data || {};
        // ensure our editable homepage sections are all present
        const savedSections = (s.homepage?.sections || []);
        const byKey = Object.fromEntries(savedSections.map((x) => [x.key, x]));
        const merged = HOMEPAGE_SECTIONS.map((d, i) => ({
          key: d.key,
          label: d.label,
          enabled: byKey[d.key]?.enabled !== false,
          order: byKey[d.key]?.order ?? i,
        })).sort((a, b) => a.order - b.order);
        setCfg({ ...s, homepage: { ...(s.homepage || {}), sections: merged } });
        // Storefront (Category) config from live store settings.
        const commerce = stRes.data?.settings?.commerce || stRes.data?.commerce || {};
        setSfCommerce(commerce);
        const storefront = commerce.storefront || {};
        setSf({
          category_style:      storefront.category_style      || "circle",
          category_font:       storefront.category_font       || "default",
          category_layout:     storefront.category_layout     || "auto",     // auto | horizontal | vertical | grid | carousel
          category_size:       storefront.category_size       ?? "",       // px, blank = default
          category_image_size: storefront.category_image_size ?? "",       // % of box, blank = 100
          category_image_fit:  storefront.category_image_fit  || "cover",  // cover=Fill | contain=Fit | fill=Stretch | none
          category_box_padding: storefront.category_box_padding ?? "",     // px inside tile around image
          category_border_width: storefront.category_border_width ?? "",   // px
          category_border_color: storefront.category_border_color || "",   // hex
          category_image_bg:   storefront.category_image_bg   || "",       // hex — image wrapper bg
          category_label_weight: storefront.category_label_weight ?? "",   // 400 / 500 / 600 / 700
          category_shadow:     storefront.category_shadow     || "hover",  // none | hover | soft | medium | strong
          category_per_row_desktop: storefront.category_per_row_desktop ?? "", // int (4-12)
          category_per_row_mobile:  storefront.category_per_row_mobile  ?? "", // int (2-6)
          category_gap:        storefront.category_gap        ?? "",       // px between tiles
          category_radius:     storefront.category_radius     ?? "",       // custom radius (overrides shape when set)
          category_bg:         storefront.category_bg         || "",       // hex tile background
          category_label_color: storefront.category_label_color || "",     // hex label color
          category_label_size: storefront.category_label_size ?? "",       // px
          category_show_image: storefront.category_show_image !== false,   // toggle category images
          section_bg:          storefront.section_bg          || "",
          card_bg:             storefront.card_bg             || "",
          section_padding:     storefront.section_padding     || "compact",
          border_radius:       storefront.border_radius       || "16",
          section_title_color: storefront.section_title_color || "",
          button_color:        storefront.button_color        || "",
        });
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <div className="p-6 md:p-8">
        <PageHeader title="UI Customization" subtitle="Restricted" />
        <div className="card-soft p-6 text-sm text-muted-foreground">
          Only Super Admins can edit customer-website UI. Store Owners manage their own products, orders and marketing under the other menu items.
        </div>
      </div>
    );
  }

  if (loading || !cfg) return <div className="p-6 md:p-8"><PageHeader title="UI Customization" /><div className="card-soft p-6 text-sm text-muted-foreground">Loading…</div></div>;

  const patchCard   = (k, v) => setCfg({ ...cfg, card:   { ...(cfg.card   || {}), [k]: v } });
  const patchHeader = (k, v) => setCfg({ ...cfg, header: { ...(cfg.header || {}), [k]: v } });
  const patchPdp    = (k, v) => setCfg({ ...cfg, pdp:    { ...(cfg.pdp    || {}), [k]: v } });
  // Typography nests under `card.typography` because the backend Pydantic
  // model only whitelists {homepage, card, header, pdp} at the top level.
  const patchTypo   = (k, v) => setCfg({
    ...cfg,
    card: { ...(cfg.card || {}), typography: { ...(cfg.card?.typography || {}), [k]: v } },
  });

  const patchSf = (k, v) => { setSf({ ...(sf || {}), [k]: v }); setSfDirty(true); };

  const moveSection = (idx, dir) => {
    const list = [...cfg.homepage.sections];
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    list.forEach((s, i) => { s.order = i; });
    setCfg({ ...cfg, homepage: { ...cfg.homepage, sections: list } });
  };
  const toggleSection = (idx) => {
    const list = [...cfg.homepage.sections];
    list[idx] = { ...list[idx], enabled: !list[idx].enabled };
    setCfg({ ...cfg, homepage: { ...cfg.homepage, sections: list } });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        homepage: { sections: cfg.homepage.sections.map((s, i) => ({ key: s.key, enabled: s.enabled, order: i })) },
        card: cfg.card, header: cfg.header, pdp: cfg.pdp,
      };
      await api.patch("/ui-customization", payload);
      // Sprint — Category tab. Persist storefront values (shape, colors, size,
      // spacing, radius, etc.) into settings.commerce.storefront so both this
      // page AND Store Settings → Storefront read the same source of truth.
      if (sf && sfDirty) {
        // Strip empty-string values so we don't overwrite defaults with blanks.
        const cleaned = Object.fromEntries(
          Object.entries(sf).filter(([, v]) => v !== "" && v !== null && v !== undefined),
        );
        await api.patch("/store/settings/commerce", { ...(sfCommerce || {}), storefront: cleaned });
        setSfDirty(false);
      }
      await refresh(); // instantly apply to storefront
      toast.success("UI customization saved · applied live");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="sa-shell p-3 sm:p-6 md:p-8 max-w-6xl w-full overflow-x-hidden bg-white" data-testid="ui-customization-page">
      <div className="flex items-start gap-3 mb-6" data-testid="ui-header">
        <div className="w-10 h-10 rounded-2xl bg-slate-900 text-white grid place-items-center shrink-0">
          <Palette className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold leading-none">Super Admin</div>
          <div className="text-[20px] font-semibold text-slate-900 leading-tight">Theme Builder</div>
          <div className="text-[12px] text-slate-500 mt-0.5">Design your customer website — colors, typography, layout — all live.</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a href="/" target="_blank" rel="noreferrer" className="sa-btn-ghost h-9 px-3 inline-flex items-center gap-1.5 text-[13px]" data-testid="preview-storefront">
            <Eye className="w-4 h-4" /> Preview
          </a>
          <button onClick={save} disabled={saving} className="sa-btn-primary h-9 px-4 inline-flex items-center gap-1.5 text-[13px] disabled:opacity-60" data-testid="save-ui-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save & Apply"}
          </button>
        </div>
      </div>

      <Tabs defaultValue="theme" className="w-full">
        {/* Sprint — Mobile friendly: horizontally scrollable tab strip so 6+
            triggers never overflow the viewport on 320-768px devices. */}
        <TabsList className="mb-4 w-full overflow-x-auto flex-nowrap justify-start gap-1 h-auto p-1 no-scrollbar rounded-xl">
          <TabsTrigger value="theme"      data-testid="tab-theme"      className="min-h-[44px] px-4 whitespace-nowrap flex-shrink-0">Theme</TabsTrigger>
          <TabsTrigger value="homepage"   data-testid="tab-homepage"   className="min-h-[44px] px-4 whitespace-nowrap flex-shrink-0">Homepage</TabsTrigger>
          <TabsTrigger value="category"   data-testid="tab-category"   className="min-h-[44px] px-4 whitespace-nowrap flex-shrink-0">Category</TabsTrigger>
          <TabsTrigger value="card"       data-testid="tab-card"       className="min-h-[44px] px-4 whitespace-nowrap flex-shrink-0">Product Card</TabsTrigger>
          <TabsTrigger value="typography" data-testid="tab-typography" className="min-h-[44px] px-4 whitespace-nowrap flex-shrink-0">Typography</TabsTrigger>
          <TabsTrigger value="header"     data-testid="tab-header"     className="min-h-[44px] px-4 whitespace-nowrap flex-shrink-0">Header</TabsTrigger>
          <TabsTrigger value="pdp"        data-testid="tab-pdp"        className="min-h-[44px] px-4 whitespace-nowrap flex-shrink-0">Product Details</TabsTrigger>
        </TabsList>

        {/* Theme — visual color/typography builder with live preview */}
        <TabsContent value="theme">
          <ThemeBuilder sf={sf} onChange={patchSf} card={cfg.card} onCardChange={patchCard} typo={cfg.card?.typography || {}} onTypoChange={patchTypo} />
        </TabsContent>

        {/* Homepage */}
        <TabsContent value="homepage">
          <div className="card-soft p-3 sm:p-4 md:p-6">
            <div className="text-sm text-muted-foreground mb-3">Reorder the sections (use ↑/↓) and toggle visibility.</div>
            <div className="space-y-2">
              {cfg.homepage.sections.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2 sm:gap-3 rounded-lg border bg-card px-2 sm:px-3 py-2 min-w-0"
                     data-testid={`section-${s.key}`}>
                  <GripVertical className="w-4 h-4 text-muted-foreground shrink-0 hidden sm:block" />
                  <div className="flex-1 min-w-0 text-sm font-medium truncate">{s.label}</div>
                  <Badge variant="secondary" className="font-mono text-[10px] shrink-0">#{i + 1}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => moveSection(i, -1)} disabled={i === 0}
                          className="min-h-[44px] min-w-[44px] shrink-0"
                          data-testid={`up-${s.key}`}>↑</Button>
                  <Button size="sm" variant="ghost" onClick={() => moveSection(i, +1)} disabled={i === cfg.homepage.sections.length - 1}
                          className="min-h-[44px] min-w-[44px] shrink-0"
                          data-testid={`down-${s.key}`}>↓</Button>
                  <Switch checked={s.enabled} onCheckedChange={() => toggleSection(i)}
                          className="shrink-0"
                          data-testid={`toggle-${s.key}`} />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Category — Sprint (restored). Same fields power StoreSettings →
            Storefront tab; both edit `settings.commerce.storefront`. */}
        <TabsContent value="category">
          <div className="card-soft p-3 sm:p-4 md:p-6 space-y-6" data-testid="category-panel">
            <div>
              <Label className="mb-2 block">Category Layout</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: "auto",       label: "Auto (Responsive)" },
                  { key: "horizontal", label: "Horizontal" },
                  { key: "vertical",   label: "Vertical" },
                  { key: "grid",       label: "Grid" },
                  { key: "carousel",   label: "Slider / Carousel" },
                ].map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => patchSf("category_layout", o.key)}
                    className={`min-h-[44px] px-4 py-2 rounded-lg border text-sm font-medium transition ${sf?.category_layout === o.key ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}
                    data-testid={`cat-layout-${o.key}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1.5">Horizontal = swipeable rail · Vertical = stacked list · Grid = dense tile grid · Slider = auto-advancing carousel · Auto = current responsive default.</div>
            </div>

            <div>
              <Label className="mb-2 block">Category tile shape</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: "circle",  label: "Circle",  sample: "rounded-full" },
                  { key: "rounded", label: "Rounded", sample: "rounded-2xl" },
                  { key: "square",  label: "Square",  sample: "rounded-md" },
                ].map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => patchSf("category_style", o.key)}
                    className={`min-h-[44px] px-4 py-2 rounded-lg border text-sm font-medium transition inline-flex items-center gap-2 ${sf?.category_style === o.key ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}
                    data-testid={`cat-style-${o.key}`}
                  >
                    <span className={`w-6 h-6 bg-secondary block ${o.sample}`} />
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Category label font</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: "default",   label: "Default",   sample: "font-medium" },
                  { key: "display",   label: "Display",   sample: "font-display italic" },
                  { key: "semibold",  label: "Bold",      sample: "font-bold" },
                  { key: "condensed", label: "Condensed", sample: "font-semibold tracking-tighter" },
                ].map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => patchSf("category_font", o.key)}
                    className={`min-h-[44px] px-4 py-2 rounded-lg border text-sm transition inline-flex items-center gap-2 ${sf?.category_font === o.key ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}
                    data-testid={`cat-font-${o.key}`}
                  >
                    <span className={`text-xs ${o.sample}`}>Aa</span>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Section padding</Label>
              <div className="flex flex-wrap gap-2">
                {["compact", "comfy", "roomy"].map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => patchSf("section_padding", k)}
                    className={`min-h-[44px] px-4 py-2 rounded-lg border text-sm font-medium capitalize transition ${sf?.section_padding === k ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}
                    data-testid={`cat-padding-${k}`}
                  >{k}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Toggle label="Show category images" value={sf?.category_show_image !== false} onChange={(v) => patchSf("category_show_image", v)} testId="cat-show-image" />
              <NumberField label="Box size (px)"       value={sf?.category_size}       onChange={(v) => patchSf("category_size", v)}       placeholder="62" testId="cat-size" />
              <NumberField label="Image size (% of box)" value={sf?.category_image_size} onChange={(v) => patchSf("category_image_size", v)} placeholder="100" testId="cat-image-size" />
              <SelectField label="Image position (fit)" value={sf?.category_image_fit || "cover"} options={["cover", "contain", "fill", "none"]} onChange={(v) => patchSf("category_image_fit", v)} testId="cat-image-fit" />
              <NumberField label="Box padding (px)"    value={sf?.category_box_padding} onChange={(v) => patchSf("category_box_padding", v)} placeholder="0" testId="cat-box-padding" />
              <NumberField label="Box gap / spacing (px)" value={sf?.category_gap}     onChange={(v) => patchSf("category_gap", v)}        placeholder="12" testId="cat-gap" />
              <NumberField label="Border radius (px, custom)" value={sf?.category_radius} onChange={(v) => patchSf("category_radius", v)}  placeholder="auto (uses shape)" testId="cat-radius" />
              <NumberField label="Border width (px)"   value={sf?.category_border_width} onChange={(v) => patchSf("category_border_width", v)} placeholder="0" testId="cat-border-width" />
              <div>
                <Label>Border color</Label>
                <Input type="color" value={sf?.category_border_color || "#e5e7eb"} onChange={(e) => patchSf("category_border_color", e.target.value)} className="mt-1.5 h-11" data-testid="cat-border-color" />
              </div>
              <div>
                <Label>Box background</Label>
                <Input type="color" value={sf?.category_bg || "#faf7f4"} onChange={(e) => patchSf("category_bg", e.target.value)} className="mt-1.5 h-11" data-testid="cat-bg" />
              </div>
              <div>
                <Label>Image background</Label>
                <Input type="color" value={sf?.category_image_bg || "#ffffff"} onChange={(e) => patchSf("category_image_bg", e.target.value)} className="mt-1.5 h-11" data-testid="cat-image-bg" />
              </div>
              <SelectField label="Shadow" value={sf?.category_shadow || "hover"} options={["none", "hover", "soft", "medium", "strong"]} onChange={(v) => patchSf("category_shadow", v)} testId="cat-shadow" />
              <NumberField label="Label size (px)"     value={sf?.category_label_size} onChange={(v) => patchSf("category_label_size", v)} placeholder="11" testId="cat-label-size" />
              <NumberField label="Label font weight"   value={sf?.category_label_weight} onChange={(v) => patchSf("category_label_weight", v)} placeholder="500" testId="cat-label-weight" />
              <div>
                <Label>Label color</Label>
                <Input type="color" value={sf?.category_label_color || "#141414"} onChange={(e) => patchSf("category_label_color", e.target.value)} className="mt-1.5 h-11" data-testid="cat-label-color" />
              </div>
              <NumberField label="Categories / row (Desktop)" value={sf?.category_per_row_desktop} onChange={(v) => patchSf("category_per_row_desktop", v)} placeholder="10" testId="cat-per-row-desktop" />
              <NumberField label="Categories / row (Mobile)"  value={sf?.category_per_row_mobile}  onChange={(v) => patchSf("category_per_row_mobile", v)}  placeholder="4"  testId="cat-per-row-mobile" />
              <div>
                <Label>Section background</Label>
                <Input type="color" value={sf?.section_bg || "#faf7f4"} onChange={(e) => patchSf("section_bg", e.target.value)} className="mt-1.5 h-11" data-testid="cat-section-bg" />
              </div>
              <div>
                <Label>Card background</Label>
                <Input type="color" value={sf?.card_bg || "#ffffff"} onChange={(e) => patchSf("card_bg", e.target.value)} className="mt-1.5 h-11" data-testid="cat-card-bg" />
              </div>
              <div>
                <Label>Section title color</Label>
                <Input type="color" value={sf?.section_title_color || "#141414"} onChange={(e) => patchSf("section_title_color", e.target.value)} className="mt-1.5 h-11" data-testid="cat-title-color" />
              </div>
              <NumberField label="Card border radius (px)" value={sf?.border_radius}  onChange={(v) => patchSf("border_radius", v)} placeholder="16" testId="cat-card-radius" />
              <div>
                <Label>Button color</Label>
                <Input type="color" value={sf?.button_color || "#dc1f26"} onChange={(e) => patchSf("button_color", e.target.value)} className="mt-1.5 h-11" data-testid="cat-btn-color" />
              </div>
            </div>

            <div className="text-[11px] text-muted-foreground">
              These settings are shared with Store Settings → Storefront tab. Save & Apply above persists them site-wide.
            </div>
          </div>
        </TabsContent>

        {/* Product Card */}
        <TabsContent value="card">
          <div className="card-soft p-3 sm:p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Toggle label="Show Discount Badge" value={cfg.card?.show_discount_badge !== false}
                    onChange={(v) => patchCard("show_discount_badge", v)} testId="card-discount-badge" />
            <SelectField label="Badge Position" value={cfg.card?.badge_position || "top-left"}
                         options={["top-left", "top-right", "bottom-left", "bottom-right"]}
                         onChange={(v) => patchCard("badge_position", v)} testId="card-badge-pos" />
            <SelectField label="Badge Style" value={cfg.card?.badge_style || "gradient"}
                         options={["gradient", "solid", "outline"]}
                         onChange={(v) => patchCard("badge_style", v)} testId="card-badge-style" />
            <SelectField label='Show "%" or "OFF"' value={cfg.card?.discount_label || "% OFF"}
                         options={["% OFF", "%", "OFF"]}
                         onChange={(v) => patchCard("discount_label", v)} testId="card-discount-label" />
            <TextField label="Delivery Badge Text" value={cfg.card?.delivery_badge_text || ""}
                       onChange={(v) => patchCard("delivery_badge_text", v)}
                       placeholder="10 min / 15 min / Fresh / Express" testId="card-delivery-text" />
            <SelectField label="Delivery Badge Position" value={cfg.card?.delivery_badge_position || "top-right"}
                         options={["top-left", "top-right", "bottom-left", "bottom-right"]}
                         onChange={(v) => patchCard("delivery_badge_position", v)} testId="card-delivery-pos" />
            <Toggle label="Show MRP (strike-through)" value={cfg.card?.show_mrp !== false}
                    onChange={(v) => patchCard("show_mrp", v)} testId="card-mrp" />
            <Toggle label="Show Discount %" value={cfg.card?.show_discount_pct !== false}
                    onChange={(v) => patchCard("show_discount_pct", v)} testId="card-discount-pct" />
            <Toggle label="Show Weight" value={cfg.card?.show_weight !== false}
                    onChange={(v) => patchCard("show_weight", v)} testId="card-weight" />
            <Toggle label="Show Brand" value={!!cfg.card?.show_brand}
                    onChange={(v) => patchCard("show_brand", v)} testId="card-brand" />
            <TextField label="ADD Button Text" value={cfg.card?.add_button_text || "ADD"}
                       onChange={(v) => patchCard("add_button_text", v)} testId="card-add-text" />
            <SelectField label="ADD Button Style" value={cfg.card?.add_button_style || "outline"}
                         options={["outline", "solid", "pill"]}
                         onChange={(v) => patchCard("add_button_style", v)} testId="card-add-style" />
            <SelectField label="ADD Button Variant" value={cfg.card?.add_button_variant || "text"}
                         options={["text", "plus", "cart", "text_plus", "custom"]}
                         onChange={(v) => patchCard("add_button_variant", v)} testId="card-add-variant" />
            <SelectField label="Product Image Position" value={cfg.card?.image_position || "center"}
                         options={["top", "center", "bottom", "custom"]}
                         onChange={(v) => patchCard("image_position", v)} testId="card-image-pos" />
            {(cfg.card?.image_position === "custom") && (
              <TextField label="Image Custom Offset Y (0–100%)"
                         value={String(cfg.card?.image_offset_y ?? 50)}
                         onChange={(v) => patchCard("image_offset_y", Math.max(0, Math.min(100, Number(v || 0))))}
                         placeholder="50" testId="card-image-offset-y" />
            )}

            {/* ─── Sprint 55: Card layout ─────────────────────────── */}
            <div className="md:col-span-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Card Layout</div>
            <NumberField label="Card Radius (px)"   value={cfg.card?.radius}       onChange={(v) => patchCard("radius", v)}       placeholder="16" testId="card-radius" />
            <SelectField label="Card Shadow"        value={cfg.card?.shadow || "soft"} options={["none","soft","strong"]} onChange={(v) => patchCard("shadow", v)} testId="card-shadow" />
            <NumberField label="Card Padding X (px)" value={cfg.card?.padding_x}    onChange={(v) => patchCard("padding_x", v)}    placeholder="6"  testId="card-pad-x" />
            <NumberField label="Card Padding Y (px)" value={cfg.card?.padding_y}    onChange={(v) => patchCard("padding_y", v)}    placeholder="6"  testId="card-pad-y" />
            <SelectField label="Image Aspect Ratio" value={cfg.card?.image_aspect || "1/1"} options={["1/1","4/3","3/4","5/4","4/5"]} onChange={(v) => patchCard("image_aspect", v)} testId="card-image-aspect" />
            <NumberField label="Image Padding (px)" value={cfg.card?.image_padding} onChange={(v) => patchCard("image_padding", v)} placeholder="2" testId="card-image-pad" />

            {/* ─── Sprint 55: Name / Price / MRP ─────────────────── */}
            <div className="md:col-span-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Product Name</div>
            <NumberField label="Name Font Size (px)" value={cfg.card?.name_size}   onChange={(v) => patchCard("name_size", v)}   placeholder="12" testId="card-name-size" />
            <NumberField label="Name Weight"         value={cfg.card?.name_weight} onChange={(v) => patchCard("name_weight", v)} placeholder="600" testId="card-name-weight" />
            <SelectField label="Name Lines"          value={String(cfg.card?.name_lines || 2)} options={["1","2","3"]} onChange={(v) => patchCard("name_lines", Number(v))} testId="card-name-lines" />

            <div className="md:col-span-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Price</div>
            <NumberField label="Price Font Size (px)" value={cfg.card?.price_size}   onChange={(v) => patchCard("price_size", v)}   placeholder="13.5" testId="card-price-size" />
            <NumberField label="Price Weight"         value={cfg.card?.price_weight} onChange={(v) => patchCard("price_weight", v)} placeholder="700"  testId="card-price-weight" />
            <TextField   label="Price Color (CSS)"    value={cfg.card?.price_color || ""} onChange={(v) => patchCard("price_color", v)} placeholder="#111827 or hsl(...)" testId="card-price-color" />
            <TextField   label="Currency Symbol"      value={cfg.card?.currency_symbol ?? "₹"} onChange={(v) => patchCard("currency_symbol", v)} placeholder="₹" testId="card-currency" />
            <SelectField label="Currency Position"    value={cfg.card?.currency_position || "before"} options={["before","after"]} onChange={(v) => patchCard("currency_position", v)} testId="card-currency-pos" />

            <div className="md:col-span-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">MRP</div>
            <NumberField label="MRP Font Size (px)"   value={cfg.card?.mrp_size}     onChange={(v) => patchCard("mrp_size", v)}     placeholder="10"  testId="card-mrp-size" />
            <NumberField label="MRP Weight"           value={cfg.card?.mrp_weight}   onChange={(v) => patchCard("mrp_weight", v)}   placeholder="400" testId="card-mrp-weight" />
            <TextField   label="MRP Color (CSS)"      value={cfg.card?.mrp_color || ""} onChange={(v) => patchCard("mrp_color", v)} placeholder="hsl(215 15% 60%)" testId="card-mrp-color" />
            <NumberField label="Strike-through Width (px)" value={cfg.card?.mrp_strike_width} onChange={(v) => patchCard("mrp_strike_width", v)} placeholder="1" testId="card-mrp-strike" />

            {/* ─── Sprint 55: Button dimensions ───────────────────── */}
            <div className="md:col-span-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">ADD / Qty Button</div>
            <NumberField label="Button Width (px)"    value={cfg.card?.button_width}  onChange={(v) => patchCard("button_width", v)}  placeholder="68" testId="card-btn-w" />
            <NumberField label="Button Height (px)"   value={cfg.card?.button_height} onChange={(v) => patchCard("button_height", v)} placeholder="32" testId="card-btn-h" />
            <NumberField label="Button Radius (px)"   value={cfg.card?.button_radius} onChange={(v) => patchCard("button_radius", v)} placeholder="8"  testId="card-btn-r" />

            {/* ─── Sprint 57: Add button colors ───────────────────── */}
            <TextField label="Add Button Background"     value={cfg.card?.add_bg_color || ""}     onChange={(v) => patchCard("add_bg_color", v)}     placeholder="#FFF0F0 or transparent" testId="card-add-bg" />
            <TextField label="Add Button Border Color"   value={cfg.card?.add_border_color || ""} onChange={(v) => patchCard("add_border_color", v)} placeholder="#FF0302"                testId="card-add-border" />
            <TextField label="Add Button Text Color"     value={cfg.card?.add_text_color || ""}   onChange={(v) => patchCard("add_text_color", v)}   placeholder="#FF0302"                testId="card-add-text-color" />
            <TextField label="Add Button Hover Bg"       value={cfg.card?.add_hover_color || ""}  onChange={(v) => patchCard("add_hover_color", v)}  placeholder="#FF0302"                testId="card-add-hover" />
            <SelectField label="Add Button Shadow"       value={cfg.card?.add_shadow || "none"}   options={["none","soft","strong","glow"]} onChange={(v) => patchCard("add_shadow", v)} testId="card-add-shadow" />
            <NumberField label="Add Button Font Size (px)" value={cfg.card?.add_font_size}        onChange={(v) => patchCard("add_font_size", v)}    placeholder="12"                     testId="card-add-fs" />
            <NumberField label="Add Button Font Weight"    value={cfg.card?.add_font_weight}      onChange={(v) => patchCard("add_font_weight", v)}  placeholder="700"                    testId="card-add-fw" />
            <SelectField label="Add Button Position"     value={cfg.card?.add_button_position || "inline"}
                         options={["inline","bottom-right","bottom-center","image-bottom","image-top-right","image-bottom-right","floating"]}
                         onChange={(v) => patchCard("add_button_position", v)} testId="card-add-position" />

            {/* ─── Sprint 57: Discount badge full customization ───── */}
            <div className="md:col-span-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Discount Badge</div>
            <TextField label="Discount Badge Background" value={cfg.card?.discount_bg_color || ""}   onChange={(v) => patchCard("discount_bg_color", v)}   placeholder="Leave blank for gradient" testId="card-disc-bg" />
            <TextField label="Discount Badge Text Color" value={cfg.card?.discount_text_color || ""} onChange={(v) => patchCard("discount_text_color", v)} placeholder="#FFFFFF" testId="card-disc-text" />
            <NumberField label="Discount Font Size (px)" value={cfg.card?.discount_font_size}        onChange={(v) => patchCard("discount_font_size", v)}  placeholder="11" testId="card-disc-fs" />
            <NumberField label="Discount Font Weight"    value={cfg.card?.discount_font_weight}      onChange={(v) => patchCard("discount_font_weight", v)} placeholder="800" testId="card-disc-fw" />
            <NumberField label="Discount Border Radius (px)" value={cfg.card?.discount_radius}       onChange={(v) => patchCard("discount_radius", v)}     placeholder="6" testId="card-disc-r" />
            <SelectField label="Discount Badge Shape"    value={cfg.card?.discount_shape || "rounded"} options={["rounded","pill","rectangle","circle","tag"]} onChange={(v) => patchCard("discount_shape", v)} testId="card-disc-shape" />
            <NumberField label="Discount Width (px)"     value={cfg.card?.discount_width}            onChange={(v) => patchCard("discount_width", v)}      placeholder="auto" testId="card-disc-w" />
            <NumberField label="Discount Height (px)"    value={cfg.card?.discount_height}           onChange={(v) => patchCard("discount_height", v)}     placeholder="auto" testId="card-disc-h" />
            <SelectField label="Discount Shadow"         value={cfg.card?.discount_shadow || "soft"} options={["none","soft","strong"]} onChange={(v) => patchCard("discount_shadow", v)} testId="card-disc-shadow" />

            {/* ─── Sprint 57: Delivery badge full customization ───── */}
            <div className="md:col-span-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Express Delivery Badge</div>
            <Toggle label="Show Express Delivery Badge"  value={cfg.card?.show_delivery_badge === true} onChange={(v) => patchCard("show_delivery_badge", v)} testId="card-del-show" />
            <SelectField label="Position"                value={cfg.card?.delivery_badge_position || "top-right"}
                         options={["top-left","top-right","bottom-left","bottom-right","inside-image","outside-image"]}
                         onChange={(v) => patchCard("delivery_badge_position", v)} testId="card-del-pos" />
            <TextField label="Delivery Badge Background" value={cfg.card?.delivery_bg_color || ""}    onChange={(v) => patchCard("delivery_bg_color", v)}    placeholder="rgba(255,255,255,0.95)" testId="card-del-bg" />
            <TextField label="Delivery Badge Text Color" value={cfg.card?.delivery_text_color || ""}  onChange={(v) => patchCard("delivery_text_color", v)}  placeholder="#111111" testId="card-del-text" />
            <NumberField label="Delivery Radius (px)"    value={cfg.card?.delivery_radius}            onChange={(v) => patchCard("delivery_radius", v)}      placeholder="6" testId="card-del-r" />
            <NumberField label="Delivery Font Size (px)" value={cfg.card?.delivery_font_size}         onChange={(v) => patchCard("delivery_font_size", v)}   placeholder="10" testId="card-del-fs" />
            <Toggle label="Show Delivery Timer Icon"     value={cfg.card?.delivery_show_icon !== false} onChange={(v) => patchCard("delivery_show_icon", v)} testId="card-del-icon" />
          </div>
        </TabsContent>

        {/* Typography (Sprint 55) */}
        <TabsContent value="typography">
          <div className="card-soft p-3 sm:p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 text-xs text-muted-foreground">
              Fonts apply live across the customer site. Provide any valid CSS
              <code className="mx-1 px-1 rounded bg-muted">font-family</code>
              string, e.g. <em>Inter, sans-serif</em> or <em>"DM Serif Display", serif</em>.
              Fonts must already be loaded by the site (Google Fonts / self-hosted).
            </div>
            <TextField   label="Body Font Family"    value={cfg.card?.typography?.body_font_family || ""}    onChange={(v) => patchTypo("body_font_family", v)}    placeholder="Inter, sans-serif" testId="typo-body-family" />
            <TextField   label="Heading Font Family" value={cfg.card?.typography?.heading_font_family || ""} onChange={(v) => patchTypo("heading_font_family", v)} placeholder="'DM Serif Display', serif" testId="typo-heading-family" />
            <TextField   label="Product Name Font"   value={cfg.card?.typography?.product_font_family || ""} onChange={(v) => patchTypo("product_font_family", v)} placeholder="Inter, sans-serif" testId="typo-product-family" />
            <TextField   label="Price Font Family"   value={cfg.card?.typography?.price_font_family || ""}   onChange={(v) => patchTypo("price_font_family", v)}   placeholder="'Inter', sans-serif" testId="typo-price-family" />
            <TextField   label="Button Font Family"  value={cfg.card?.typography?.button_font_family || ""}  onChange={(v) => patchTypo("button_font_family", v)}  placeholder="Inter, sans-serif" testId="typo-button-family" />
            <NumberField label="Body Font Size (px)" value={cfg.card?.typography?.body_font_size}            onChange={(v) => patchTypo("body_font_size", v)}      placeholder="14"  testId="typo-body-size" />
            <NumberField label="Body Font Weight"    value={cfg.card?.typography?.body_font_weight}          onChange={(v) => patchTypo("body_font_weight", v)}    placeholder="400" testId="typo-body-weight" />
            <NumberField label="Body Letter Spacing (px)" value={cfg.card?.typography?.body_letter_spacing}  onChange={(v) => patchTypo("body_letter_spacing", v)} placeholder="0" testId="typo-body-tracking" />
            <NumberField label="Body Line Height"    value={cfg.card?.typography?.body_line_height}          onChange={(v) => patchTypo("body_line_height", v)}    placeholder="1.5" step="0.05" testId="typo-body-leading" />
          </div>
        </TabsContent>

        {/* Header */}
        <TabsContent value="header">
          <div className="card-soft p-3 sm:p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="Search Placeholder" value={cfg.header?.search_placeholder || ""}
                       onChange={(v) => patchHeader("search_placeholder", v)}
                       placeholder='Search "atta", "milk"…' testId="header-search-ph" />
            <TextField label="Delivery Text Prefix" value={cfg.header?.delivery_text || ""}
                       onChange={(v) => patchHeader("delivery_text", v)}
                       placeholder="Delivery in" testId="header-delivery-text" />
            <Toggle label="Show Delivery Timer" value={cfg.header?.show_delivery_timer !== false}
                    onChange={(v) => patchHeader("show_delivery_timer", v)} testId="header-timer" />
            <Toggle label="Show Category Icons" value={cfg.header?.show_category_icons !== false}
                    onChange={(v) => patchHeader("show_category_icons", v)} testId="header-cat-icons" />
          </div>
        </TabsContent>

        {/* PDP */}
        <TabsContent value="pdp">
          <div className="card-soft p-3 sm:p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label='"Sold by" Label' value={cfg.pdp?.sold_by_text || ""}
                       onChange={(v) => patchPdp("sold_by_text", v)}
                       placeholder="Sold by" testId="pdp-sold-by-text" />
            <TextField label="Offer Text" value={cfg.pdp?.offer_text || ""}
                       onChange={(v) => patchPdp("offer_text", v)}
                       placeholder="Extra 10% off with UPI…" testId="pdp-offer-text" />
            <TextField label="Delivery Message" value={cfg.pdp?.delivery_message || ""}
                       onChange={(v) => patchPdp("delivery_message", v)}
                       placeholder="Free delivery on orders above ₹499" testId="pdp-delivery-msg" />
            <div className="md:col-span-2">
              <Label>Trust Badges (comma separated)</Label>
              <Input value={(cfg.pdp?.trust_badges || []).join(", ")}
                     onChange={(e) => patchPdp("trust_badges", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                     placeholder="100% Fresh, Easy Returns, Secure Payments"
                     className="mt-1.5" data-testid="pdp-trust-badges" />
              <div className="text-xs text-muted-foreground mt-1">Up to 3 badges shown below the ADD button.</div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-4 text-xs text-muted-foreground flex items-center gap-2">
        <RotateCcw className="w-3.5 h-3.5" /> Changes apply live to the customer website — no code changes, no redeploy needed.
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between rounded-lg border bg-card px-3 py-2.5 min-h-[52px]">
      <Label className="text-sm">{label}</Label>
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
             placeholder={placeholder} className="mt-1.5 h-11" data-testid={testId} />
    </div>
  );
}

function SelectField({ label, value, options, onChange, testId }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5 h-11" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function NumberField({ label, value, onChange, placeholder, testId, step }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <Input
        type="number"
        step={step || "1"}
        value={value ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? undefined : Number(raw));
        }}
        placeholder={placeholder}
        className="mt-1.5 h-11"
        data-testid={testId}
      />
    </div>
  );
}

/* ─── Theme Builder (Phase 2) ────────────────────────────────────────────
   Visual color pickers + live component preview. Writes to the same
   `settings.commerce.storefront` and `ui-customization.card` keys the other
   tabs already use, so no backend changes are required.
   ──────────────────────────────────────────────────────────────────────── */
function ThemeBuilder({ sf, onChange, card, onCardChange, typo, onTypoChange }) {
  const button   = sf?.button_color        || "#dc1f26";
  const sectionT = sf?.section_title_color || "#141414";
  const sectionB = sf?.section_bg          || "#faf7f4";
  const cardBg   = sf?.card_bg             || "#ffffff";
  const priceColor = card?.price_color || "#111827";
  const addBg      = card?.add_bg_color || "#FFF0F0";
  const addBorder  = card?.add_border_color || "#dc1f26";
  const addText    = card?.add_text_color || "#dc1f26";
  const discountBg = card?.discount_bg_color || "#dc1f26";
  const bodyFont   = typo?.body_font_family || "Inter, sans-serif";

  const swatches = [
    { label: "Primary button",  value: button,   onChange: (v) => onChange("button_color", v),        testId: "theme-btn-color" },
    { label: "Section title",   value: sectionT, onChange: (v) => onChange("section_title_color", v), testId: "theme-title-color" },
    { label: "Section bg",      value: sectionB, onChange: (v) => onChange("section_bg", v),          testId: "theme-section-bg" },
    { label: "Card bg",         value: cardBg,   onChange: (v) => onChange("card_bg", v),             testId: "theme-card-bg" },
    { label: "Price color",     value: priceColor, onChange: (v) => onCardChange("price_color", v),   testId: "theme-price-color" },
    { label: "Discount badge",  value: discountBg, onChange: (v) => onCardChange("discount_bg_color", v), testId: "theme-disc-bg" },
    { label: "ADD button bg",   value: addBg,      onChange: (v) => onCardChange("add_bg_color", v),  testId: "theme-add-bg" },
    { label: "ADD button text", value: addText,    onChange: (v) => onCardChange("add_text_color", v),testId: "theme-add-text" },
  ];

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_460px] gap-6" data-testid="theme-builder">
      {/* Controls */}
      <div className="space-y-6">
        <div className="sa-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-slate-500" />
            <div className="text-[13px] font-semibold text-slate-900">Brand colors</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {swatches.map((s) => (
              <ColorSwatch key={s.testId} label={s.label} value={s.value} onChange={s.onChange} testId={s.testId} />
            ))}
          </div>
        </div>

        <div className="sa-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="w-4 h-4 text-slate-500" />
            <div className="text-[13px] font-semibold text-slate-900">Typography</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Body font</Label>
              <Input value={typo?.body_font_family || ""} onChange={(e) => onTypoChange("body_font_family", e.target.value)} placeholder="Inter, sans-serif" className="mt-1.5 h-10 rounded-xl border-slate-200" data-testid="theme-body-font" />
            </div>
            <div>
              <Label className="text-sm">Heading font</Label>
              <Input value={typo?.heading_font_family || ""} onChange={(e) => onTypoChange("heading_font_family", e.target.value)} placeholder="'DM Serif Display', serif" className="mt-1.5 h-10 rounded-xl border-slate-200" data-testid="theme-heading-font" />
            </div>
            <div>
              <Label className="text-sm">Body size (px)</Label>
              <Input type="number" value={typo?.body_font_size ?? ""} onChange={(e) => onTypoChange("body_font_size", e.target.value === "" ? undefined : Number(e.target.value))} placeholder="14" className="mt-1.5 h-10 rounded-xl border-slate-200" data-testid="theme-body-size" />
            </div>
            <div>
              <Label className="text-sm">Body weight</Label>
              <Input type="number" value={typo?.body_font_weight ?? ""} onChange={(e) => onTypoChange("body_font_weight", e.target.value === "" ? undefined : Number(e.target.value))} placeholder="400" className="mt-1.5 h-10 rounded-xl border-slate-200" data-testid="theme-body-weight" />
            </div>
          </div>
        </div>

        <div className="sa-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-slate-500" />
            <div className="text-[13px] font-semibold text-slate-900">Corner radius</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm">Card radius (px)</Label>
              <Input type="number" value={sf?.border_radius ?? ""} onChange={(e) => onChange("border_radius", e.target.value)} placeholder="16" className="mt-1.5 h-10 rounded-xl border-slate-200" data-testid="theme-card-radius" />
            </div>
            <div>
              <Label className="text-sm">Button radius (px)</Label>
              <Input type="number" value={card?.button_radius ?? ""} onChange={(e) => onCardChange("button_radius", e.target.value === "" ? undefined : Number(e.target.value))} placeholder="8" className="mt-1.5 h-10 rounded-xl border-slate-200" data-testid="theme-btn-radius" />
            </div>
            <div>
              <Label className="text-sm">Discount radius (px)</Label>
              <Input type="number" value={card?.discount_radius ?? ""} onChange={(e) => onCardChange("discount_radius", e.target.value === "" ? undefined : Number(e.target.value))} placeholder="6" className="mt-1.5 h-10 rounded-xl border-slate-200" data-testid="theme-disc-radius" />
            </div>
          </div>
        </div>
      </div>

      {/* Live preview */}
      <div className="lg:sticky lg:top-6 self-start" data-testid="theme-preview">
        <div className="sa-card overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Live preview</div>
            <div className="ml-auto text-[10.5px] font-mono text-slate-400">Storefront components</div>
          </div>
          <div className="p-5 space-y-4" style={{ background: sectionB, fontFamily: bodyFont }}>
            {/* Section title */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: sectionT, opacity: 0.7 }}>Featured</div>
              <div className="text-[20px] font-semibold" style={{ color: sectionT, fontFamily: typo?.heading_font_family || "'Fraunces', serif" }}>Best sellers</div>
            </div>
            {/* Product cards row */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { name: "Amul Butter", price: "₹58", mrp: "₹60", off: "3% OFF" },
                { name: "Basmati Rice 5kg", price: "₹499", mrp: "₹599", off: "16% OFF" },
              ].map((p, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden" style={{ background: cardBg, borderRadius: `${sf?.border_radius || 16}px` }}>
                  <div className="relative aspect-square bg-slate-100 grid place-items-center text-slate-400 text-[10px] uppercase tracking-widest">
                    Product
                    <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: discountBg, borderRadius: `${card?.discount_radius || 6}px` }}>{p.off}</div>
                  </div>
                  <div className="p-2.5">
                    <div className="text-[12px] font-semibold truncate" style={{ color: sectionT }}>{p.name}</div>
                    <div className="mt-1 flex items-baseline gap-1.5">
                      <span className="text-[13.5px] font-bold" style={{ color: priceColor }}>{p.price}</span>
                      <span className="text-[10.5px] text-slate-400 line-through">{p.mrp}</span>
                    </div>
                    <button
                      type="button"
                      className="mt-2 h-7 w-full text-[11px] font-bold border"
                      style={{
                        background: addBg,
                        color: addText,
                        borderColor: addBorder,
                        borderRadius: `${card?.button_radius || 8}px`,
                      }}
                    >
                      {card?.add_button_text || "ADD"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* Primary button */}
            <button
              type="button"
              className="w-full h-10 text-white text-[13px] font-semibold shadow-[0_10px_24px_-10px_rgba(220,31,38,0.55)]"
              style={{ background: button, borderRadius: `${card?.button_radius || 8}px` }}
            >
              Shop the sale
            </button>
            {/* Discount badge chip */}
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold text-white" style={{ background: discountBg, borderRadius: `${card?.discount_radius || 6}px` }}>
              FLASH DEAL · 40% OFF
            </div>
          </div>
        </div>
        <div className="mt-3 text-[11px] text-slate-400 text-center">Preview auto-updates as you change colors above.</div>
      </div>
    </div>
  );
}

function ColorSwatch({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{label}</Label>
      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-1.5">
        <label className="relative w-8 h-8 rounded-lg cursor-pointer overflow-hidden shrink-0" style={{ background: value }}>
          <input
            type="color"
            value={/^#[0-9a-f]{6}$/i.test(value) ? value : "#000000"}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
            data-testid={`${testId}-picker`}
          />
        </label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 border-0 shadow-none focus-visible:ring-0 px-1 font-mono text-[11.5px] uppercase"
          data-testid={testId}
        />
      </div>
    </div>
  );
}
